import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Modal, OverlayContent, useOverlayState } from 'components/Overlay';
import { getFormatDateWithDaysAgo } from 'utilities/date';
import { utagLinkPDP } from 'features/tealium';
import { modalConfig } from 'apps/pixlee/constants';
import { dateMillisToDateString } from 'features/pixlee/helpers';
import { setGenericEvent } from 'features/tealium/ga4/custom';
import { getDisplayUserName } from 'features/productQuestionsAndAnswers/utility';

import { updateUpDownVote, updateInappropriateFlag } from '../reducers';

import { reviewPropTypes } from './propTypes';
import ProductReviewRatingStars from './ProductReviewRatingStars';
import ProductReviewUpDownVote from './ProductReviewUpDownVote';
import ProductReviewInappropriateFlag from './ProductReviewInappropriateFlag';
import MediaModal from './MediaModal';
import ProductReviewCardResponse from './ProductReviewCardResponse';

import './ProductReviewCard.scss';

const MAX_REVIEW_LENGTH = 300;
const isMobile = !!(window.utag_data && window.utag_data.is_mobile == '1');
const { product_category: productCategory = [] } = window.utag_data;
const [ primaryCategory ] = productCategory;

const ProductReviewCard = ({
	review, reviewIndex = 0, sortText = '', fromSearch = false, onVoteUpdate = () => {}
}) => {

	const {
		id,
		title,
		text,
		purchaseDate,
		dateCreated,
		media,
		user,
		upVotes,
		downVotes,
		rating,
		responses,
		syndication
	} = review;

	const userName = getDisplayUserName(user);

	const reviewMedia = [ ...media.photo, ...media.video ];
	const secondaryContentClass = classNames('ProductReviewCard__secondaryContent', {
		hidden: !purchaseDate && !reviewMedia.length
	});
	const [ inappropriateFlagToggle, setInappropriateFlagToggle ] = useState(false);
	const [ upVoteAdded, setUpVoteAdded ] = useState(false);
	const [ downVoteAdded, setDownVoteAdded ] = useState(false);
	const [ selectedMediaIndex, setSelectedMediaIndex ] = useState(-1);
	const [ showReadMore, setShowReadMore ] = useState(true);
	const upvoteCountRef = useRef(upVotes);
	const downvoteCountRef = useRef(downVotes);
	let purchasedOn;

	if (purchaseDate) {

		purchasedOn = getFormatDateWithDaysAgo(purchaseDate);

		purchasedOn = purchasedOn.includes('days ago') ? purchasedOn : `on ${purchasedOn}`;

	}

	const dispatch = useDispatch();
	const mediaModalState = useOverlayState(false);

	function addUpvote () {

		if (!upVoteAdded) {

			if (fromSearch) {

				upvoteCountRef.current += 1;
				onVoteUpdate({
					reviewId: id,
					upVotes: upvoteCountRef.current,
					downVotes: downvoteCountRef.current
				});

			}

			dispatch(updateUpDownVote({ id, voteType: 'voteup' }));
			setUpVoteAdded(true);

		}

	}

	function addDownvote () {

		if (!downVoteAdded) {

			if (fromSearch) {

				downvoteCountRef.current += 1;
				onVoteUpdate({
					reviewId: id,
					upVotes: upvoteCountRef.current,
					downVotes: downvoteCountRef.current
				});

			}

			dispatch(updateUpDownVote({ id, voteType: 'votedown' }));
			setDownVoteAdded(true);

		}

	}

	function handleFlagClick () {

		if (!inappropriateFlagToggle) {

			dispatch(updateInappropriateFlag(id));
			setInappropriateFlagToggle(true);

		}

	}

	function handleMediaClick (index) {

		mediaModalState.showOverlay();
		setSelectedMediaIndex(index);
		utagLinkPDP('TurnTo')({
			eventAction: 'ReviewCard-Click-Customer-Media',
			eventLabel: `${window.shortSku}-${reviewIndex}-${sortText}`
		});
		setGenericEvent({
			event_name: 'pdp_review_customerphoto_click',
			pdp_item_id: window.shortSku,
			pdp_prime_cat: primaryCategory,
			item_position_number: index + 1
		});

	}

	useEffect(() => {

		if (!mediaModalState.show) {

			setSelectedMediaIndex(-1);

		}

	}, [ mediaModalState.show ]);

	function renderMediaDetailsModal () {

		const { showTurntoCaptions } = modalConfig.captions.product;

		const mediaList = reviewMedia.map((item) => ({
			id: item.id,
			title: showTurntoCaptions ? review.title : '',
			text: showTurntoCaptions ? review.text : '',
			author: userName,
			source: 'CUSTOMER REVIEWS',
			date: review.dateCreated ? dateMillisToDateString(review.dateCreated) : null,
			media: {
				type: item.type,
				src: item.normalUrl,
				videoSrc: item.providedLink
			}
		}));

		return (
			<MediaModal
				productSku={window.shortSku}
				activeIndex={selectedMediaIndex}
				list={mediaList}
				{...modalConfig.captions.product}
			/>
		);

	}

	function handleReadMoreClick () {

		setShowReadMore(false);
		utagLinkPDP('TurnTo')({
			eventAction: 'ReviewCard-ReadMore',
			eventLabel: `${window.shortSku}-${reviewIndex}-${sortText}`
		});

	}
	return (
		<article>
			{isMobile && (
				<div className="ProductReviewCard">
					{mediaModalState.show && (
						<Modal
							{...mediaModalState}
							className="ProductReviewCard__mediaDetailsModal"
							aria-label="Media Details Modal"
							escapeExits
							greyBackground
						>
							<OverlayContent>
								{renderMediaDetailsModal()}
							</OverlayContent>
						</Modal>
					)}
					<header className="ProductReviewCard__header">
						<div className="ProductReviewCard__rating">
							<ProductReviewRatingStars rating={rating} size="normal" styleVariant="primary" />
						</div>
						<div className="ProductReviewCard__title">
							{title}
						</div>
					</header>
					<div className="ProductReviewCard__body">
						<div className={secondaryContentClass}>
							{purchaseDate && (
								<div className="ProductReviewCard__purchaseDate">
									Purchased
									{' '}
									<span data-ugctest="purchasedate">
										{purchasedOn}
									</span>
								</div>
							)}
						</div>
						<div className="ProductReviewCard__text">
							{text.length > MAX_REVIEW_LENGTH && showReadMore ? (
								<>
									{text.slice(0, MAX_REVIEW_LENGTH)}
									<button
										type="button"
										className="ProductReviewCard__readMore"
										onClick={() => {

											handleReadMoreClick();

										}}
									>
										Read More
									</button>
								</>
							) : (
								text
							)}
						</div>

						<div className="ProductReviewCard__reviewerName">
							{userName}
							{user.badge !== null && (
								<span className="ProductAnswerCard__badge">
									{user.badge.text}
								</span>
							)}
						</div>
						{dateCreated !== null && (
							<div className="ProductReviewCard__dateCreated">
								{getFormatDateWithDaysAgo(dateCreated)}
							</div>
						)}
						<div />
						{syndication?.sourceName && (
							<div className="ProductReviewCard__syndicationInfo" data-ugctest="syndicationInfo">
								Originally posted at
								{' '}
								{syndication.sourceName}
							</div>
						)}
					</div>
					<footer className="ProductReviewCard__footer">
						<div className="ProductReviewCard__votes">
							<span className="ProductReviewCard__voteText ProductReviewCard__voteText--long">
								Was this helpful?
							</span>
							<span className="ProductReviewCard__voteText ProductReviewCard__voteText--short">
								Helpful?
							</span>
							{' '}
							<ProductReviewUpDownVote
								upVotes={fromSearch ? upvoteCountRef.current : upVotes}
								downVotes={fromSearch ? downvoteCountRef.current : downVotes}
								addUpvote={addUpvote}
								addDownvote={addDownvote}
							/>
						</div>
						<ProductReviewInappropriateFlag
							inappropriateFlagToggle={inappropriateFlagToggle}
							handleFlagClick={handleFlagClick}
						/>
					</footer>
					{reviewMedia.length > 0 && (
						<div className="ProductReviewCard__mediaGallery">
							{reviewMedia.map(({ id: mediaId, thumbnailUrl, type }, index) => {

								const isVideo = type.toLowerCase() === 'video';
								const mediaItemClass = classNames('ProductReviewCard__mediaItem', {
									'ProductReviewCard__mediaItem--video': isVideo
								});
								return (
									<button
										aria-label="Show Media Details Modal"
										tabIndex="0"
										onClick={() => handleMediaClick(index)}
										type="button"
										key={mediaId}
										className={mediaItemClass}
									>
										{isVideo ? (
											<div className="ProductReviewCard__video">
												<span className="ProductReviewCard__videoIcon lpIcon-video" />
												<img src={thumbnailUrl} alt="" />
											</div>
										) : (
											<img src={thumbnailUrl} alt="" />
										)}
									</button>
								);

							})}
						</div>
					)}
				</div>
			)}
			{!isMobile && (
				<div className="ProductReviewCard">
					{mediaModalState.show && (
						<Modal
							{...mediaModalState}
							className="ProductReviewCard__mediaDetailsModal"
							aria-label="Media Details Modal"
							escapeExits
							greyBackground
						>
							<OverlayContent>
								{renderMediaDetailsModal()}
							</OverlayContent>
						</Modal>
					)}
					<div className="ProductReviewCard__header">
						<div className="ProductReviewCard__ratingSection">
							<div className="ProductReviewCard__rating">
								<ProductReviewRatingStars rating={rating} size="normal" styleVariant="primary" />
							</div>
							<div className="ProductReviewCard__title">
								{title}
							</div>
						</div>
						{reviewMedia.length > 0 && (
							<div className="ProductReviewCard__mediaGallery">
								{reviewMedia.map(({ id: mediaId, thumbnailUrl, type }, index) => {

									const isVideo = type.toLowerCase() === 'video';
									const mediaItemClass = classNames('ProductReviewCard__mediaItem', {
										'ProductReviewCard__mediaItem--video': isVideo
									});
									return (
										<button
											aria-label="Show Media Details Modal"
											tabIndex="0"
											onClick={() => handleMediaClick(index)}
											type="button"
											key={mediaId}
											className={mediaItemClass}
										>
											{isVideo ? (
												<div className="ProductReviewCard__video">
													<span className="ProductReviewCard__videoIcon lpIcon-video" />
													<img src={thumbnailUrl} alt="" />
												</div>
											) : (
												<img src={thumbnailUrl} alt="" />
											)}
										</button>
									);

								})}
							</div>
						)}
						<div className="ProductReviewCard__text">
							{text.length > MAX_REVIEW_LENGTH && showReadMore ? (
								<p>
									{text.slice(0, MAX_REVIEW_LENGTH)}
									<button
										type="button"
										className="ProductReviewCard__readMore"
										onClick={() => {

											handleReadMoreClick();

										}}
									>
										Read More
									</button>
								</p>
							) : (
								<p>
									{text}
								</p>
							)}
						</div>
					</div>
					<div className="ProductReviewCard__body">
						<div className="ProductReviewCard__helpInfo">
							<div className="ProductReviewCard__footer">
								<div className="ProductReviewCard__votes">
									<span className="ProductReviewCard__voteText ProductReviewCard__voteText--long">
										Was this helpful?
									</span>
									<span className="ProductReviewCard__voteText ProductReviewCard__voteText--short">
										Helpful?
									</span>
									{' '}
									<ProductReviewUpDownVote
										upVotes={fromSearch ? upvoteCountRef.current : upVotes}
										downVotes={fromSearch ? downvoteCountRef.current : downVotes}
										addUpvote={addUpvote}
										addDownvote={addDownvote}
									/>
								</div>
								<ProductReviewInappropriateFlag
									inappropriateFlagToggle={inappropriateFlagToggle}
									handleFlagClick={handleFlagClick}
								/>
							</div>
							<div className="ProductReviewCard__reviewerName">
								{user.badge !== null && (
									<span className="ProductAnswerCard__badge">
										{user.badge.text}
									</span>
								)}
								{userName}
								{dateCreated !== null && (
									<div className="ProductReviewCard__dateCreated">
										<span>|</span>
										{getFormatDateWithDaysAgo(dateCreated)}
									</div>
								)}
								{syndication?.sourceName && (
									<div className="ProductReviewCard__syndicationInfo" data-ugctest="syndicationInfo">
										Originally posted at
										{' '}
										{syndication.sourceName}
									</div>
								)}
							</div>
						</div>
						<div className={secondaryContentClass}>
							{purchaseDate && (
								<div className="ProductReviewCard__purchaseDate">
									Purchased
									{' '}
									<span data-ugctest="purchasedate">
										{purchasedOn}
									</span>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
			{responses && responses.length !== 0 && <ProductReviewCardResponse response={responses[0]} />}
		</article>
	);

};

ProductReviewCard.propTypes = {
	review: reviewPropTypes,
	reviewIndex: PropTypes.number,
	sortText: PropTypes.string,
	fromSearch: PropTypes.bool,
	onVoteUpdate: PropTypes.func
};

export default ProductReviewCard;
